<script setup lang="ts">
import { computed, h, onMounted, ref, watch, type PropType, type VNode } from 'vue';
import { type RouteLocationRaw, RouterLink, useRouter } from 'vue-router';
import { NIcon, type SelectOption } from 'naive-ui';
import { IconMore, IconShare } from '@/components/design/icons';
import Layout from '@/components/design/layout/Layout.vue';
import GaiaAddButton from '@/components/design/base/GaiaAddButton.vue';
import GaiaCreateButton from '@/components/design/base/GaiaCreateButton.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCountLabel from '@/components/design/base/GaiaCountLabel.vue';
import GaiaCountPill from '@/components/design/base/GaiaCountPill.vue';
import GaiaDeleteButton from '@/components/design/base/GaiaDeleteButton.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaLink from '@/components/design/base/GaiaLink.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaPopover from '@/components/design/base/GaiaPopover.vue';
import GaiaDropdown from '@/components/design/base/GaiaDropdown.vue';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTableSkeleton from '@/components/design/composite/GaiaTableSkeleton.vue';
import GaiaColumnsMenu from '@/components/design/composite/GaiaColumnsMenu.vue';
import GaiaDataTable, {
  type GaiaDataColumn,
} from '@/components/design/composite/GaiaDataTable.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaFilterBar from '@/components/design/composite/GaiaFilterBar.vue';
import GaiaFilterChip from '@/components/design/composite/GaiaFilterChip.vue';
import GaiaFilterValuePanel, {
  type GaiaFilterValue,
} from '@/components/design/composite/GaiaFilterValuePanel.vue';
import GaiaPagination from '@/components/design/composite/GaiaPagination.vue';
import GaiaSelectionBar from '@/components/design/composite/GaiaSelectionBar.vue';
import GaiaTableRowState from '@/components/design/composite/GaiaTableRowState.vue';
import GaiaMergeField, {
  GAIA_MERGE_COLUMNS,
} from '@/components/design/domain/contractManagement/GaiaMergeField.vue';
import { joinTags, splitTags } from '@/components/design/domain/contractManagement/mergeTags';
import { gaiaContentWidth, gaiaNeutral } from '@/components/design/themes/tokens';
import { useListQuerySync } from '@/composables/useListQuerySync';
import { useRowDelete } from '@/composables/useRowDelete';
import { logger } from '@/utils/logger';
import type { FilterableSearchQuery } from '@/api';
import GaiaCheckbox from '@/components/design/base/GaiaCheckbox.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';
import {
  deleteEntity,
  entityConfigs,
  entityLabel,
  isFieldShown,
  toText,
  type EntityColumn,
  type EntityField,
  type EntityFormValues,
  type EntityRow,
} from './entityListConfig';
import EntityFormDialog from './EntityFormDialog.vue';
import MassShareContractsModal from '@/components/contractManagement/MassShareContractsModal.vue';
import {
  ACCESS_PERMISSION_ADMIN,
  ACCESS_PERMISSION_WRITE,
  allows,
  deniedReason,
} from '@/utils/accessPermission';
import type { ContractManagementEntityType } from '@/types/contractManagement/detailConfig';

/**
 * One list page for every contract-management entity, mirroring the design's
 * own shape: the six entity screens are a single "Entity List Template"
 * parameterised by type. The type comes from the route so the router stays a
 * table of paths, not a table of components.
 */
const props = defineProps({
  /** Which entity this page lists — the app-wide entity key. */
  entityType: {
    type: String as PropType<ContractManagementEntityType>,
    required: true,
  },
});

const router = useRouter();
const message = useGaiaToast();

const config = computed(() => {
  const found = entityConfigs[props.entityType];
  if (!found) throw new Error(`No list config for entity type “${props.entityType}”`);
  return found;
});

/** Singular label — read from the app's entity config, not restated here. */
const label = computed(() => entityLabel(config.value));

const {
  currentPage,
  pageSize,
  searchQuery,
  sortKey,
  sortDirection,
  sortActivated,
  columnFilters,
  setColumnFilters,
} = useListQuerySync();

const rows = ref<EntityRow[]>([]);
const totalCount = ref(0);
const filterOptions = ref<Record<string, (string | null)[]>>({});
const loading = ref(false);
const loadError = ref<string | null>(null);
/** Hide entities extracted from superseded contract revisions. */
const excludeOldRevisions = ref(true);

// --- toolbar ---------------------------------------------------------------
const filterBarOpen = ref(false);
/** Which filter columns have a chip on the bar (a chip can sit at "Any"). */
const openFilterKeys = ref<string[]>([]);
const openChip = ref<string | null>(null);
const valueSearch = ref<Record<string, string>>({});
/**
 * Which columns are on. Seeded from the config's `def` set — a column with no
 * `def` starts hidden, and `locked` is always
 * on. Held as keys, not as columns, so re-enabling one restores it to its
 * place in the config's order rather than appending it.
 */
const visibleKeys = ref<string[]>([]);

function defaultVisibleKeys(): string[] {
  return (
    config.value.columns
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- boolean OR, not a nullish fallback
      .filter(column => column.locked || column.def === true)
      .map(column => column.key)
  );
}

// --- row picking: merge (two of a kind) or share (any number, parties only) ---
const selectMode = ref<'merge' | 'share' | null>(null);
const mergeMode = computed(() => selectMode.value === 'merge');
/** Rows are being picked — checkboxes on, row clicks toggle instead of opening. */
const picking = computed(() => selectMode.value !== null);
/**
 * The picked rows themselves, not just their uids: a search or page change
 * swaps `rows`, and a pick made before it must still be readable after.
 */
const selectedRows = ref<EntityRow[]>([]);
const selected = computed(() => selectedRows.value.map(row => row.uid));
/** Contractual parties are shared like contracts; no other record is. */
const canShare = computed(() => config.value.type === 'contractual-party');
const shareOpen = ref(false);
const selectedNames = computed(() => selectedRows.value.map(row => rowName(row)));

/**
 * Merging deletes both records and sharing needs full access too, so a pick
 * the caller only reads or edits blocks the action — said, not hidden. Rows
 * without bits (every type but parties) gate nothing.
 */
function adminBlockFor(rows: EntityRow[]): string | null {
  const lacking = rows.filter(row => !allows(row.permissions, ACCESS_PERMISSION_ADMIN)).length;
  if (lacking === 0) return null;
  return rows.length === 1 || lacking === rows.length
    ? 'Needs full access'
    : `${lacking} of the selected need full access`;
}
const selectionAdminBlock = computed(() => adminBlockFor(selectedRows.value));
const mergeValues = ref<Record<string, string>>({});
const mergeOpen = ref(false);
/** Fields empty in both records are folded away until asked for. */
const mergeShowEmpty = ref(false);
/**
 * What the new record inherits. Both kinds of merge create a new record and
 * delete the two originals; a party also carries its representatives, shares
 * and signing participants across.
 */
const mergeCarryNote = computed(() =>
  config.value.type === 'contractual-party'
    ? 'Contract files, representatives, shares and signing participants move to the new record.'
    : 'Contract links from both records move to the new record.'
);
const merging = ref(false);
// Inline, not v-bind(): the merge dialog teleports out of this component's root.
const mergeRule = `1px solid ${gaiaNeutral.borderCard}`;

// --- row state -------------------------------------------------------------
const rowDelete = useRowDelete<EntityRow>();

// --- create / edit ---------------------------------------------------------
/**
 * Open state is its own flag rather than `mode === null`: the dialog keeps
 * rendering through its fade-out, and a mode that goes null mid-animation
 * would flip every label to the other branch on the way out.
 */
const formOpen = ref(false);
/** The record being edited; null while creating. */
const formRow = ref<EntityRow | null>(null);
/** The picked create preset's starting values (a party's kind). */
const formDefaults = ref<EntityFormValues>({});

const createMenuOptions = computed(() =>
  (config.value.createOptions ?? []).map(option => ({
    key: option.key,
    label: option.label,
    icon: (): VNode => h(NIcon, { component: option.icon }),
  }))
);

// --- derived ---------------------------------------------------------------
/** Columns start at the config's `def` set; the menu edits the hidden list. */
const visibleColumns = computed(() =>
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- boolean OR, not a nullish fallback
  config.value.columns.filter(column => column.locked || visibleKeys.value.includes(column.key))
);

const tableColumns = computed<GaiaDataColumn[]>(() =>
  visibleColumns.value.map(column => ({
    key: column.key,
    label: column.label,
    width: column.width,
    sortable: column.sortable,
  }))
);

const columnOptions = computed(() =>
  config.value.columns.map(column => ({
    key: column.key,
    label: column.label,
    locked: column.locked,
    visible: visibleColumns.value.some(visible => visible.key === column.key),
  }))
);

const activeFilterCount = computed(() =>
  Object.values(columnFilters.value).reduce((total, values) => total + values.length, 0)
);

const hasQuery = computed(() => searchQuery.value.trim().length > 0);

const countLabel = computed(() => {
  if (loading.value) return 'Loading';
  const [singular, plural] = config.value.unit;
  return `${totalCount.value} ${totalCount.value === 1 ? singular : plural}`;
});

const showEmpty = computed(
  () =>
    !loading.value &&
    !loadError.value &&
    rows.value.length === 0 &&
    !hasQuery.value &&
    !activeFilterCount.value
);
const showNoMatch = computed(
  () => !loading.value && !loadError.value && rows.value.length === 0 && !showEmpty.value
);
const showRows = computed(() => !loading.value && !loadError.value && rows.value.length > 0);

const noMatchTitle = computed(() =>
  hasQuery.value
    ? `No ${config.value.unit[1]} match “${searchQuery.value.trim()}”`
    : `No ${config.value.unit[1]} match these filters`
);

/** Which of the skeleton widths a cell gets, so the shimmer isn't a block. */
const skeletonWidths = ['62%', '78%', '54%', '48px', '112px', '90px', '70%', '84px', '100px'];
const skeletonCells = computed(() =>
  visibleColumns.value.map((_, position) =>
    position === 0 ? '62%' : skeletonWidths[(position * 3) % skeletonWidths.length]
  )
);

/**
 * The dialog's rows in reading order: where the records disagree first (and a
 * required field still empty), then the rest; fields empty in both are
 * counted and folded away.
 */
const mergeGroups = computed(() => {
  const conflicts = mergeRows.value.filter(
    row =>
      row.state === 'missing' ||
      (Boolean(row.leftValue) && Boolean(row.rightValue) && row.leftValue !== row.rightValue)
  );
  const rest = mergeRows.value.filter(row => !conflicts.includes(row));
  return {
    conflicts,
    filled: rest.filter(row => row.leftValue || row.rightValue),
    empty: rest.filter(row => !row.leftValue && !row.rightValue),
  };
});

/** Under a record's name in the dialog header: when it was created. */
function mergeRecordMeta(row: EntityRow | null): string {
  return row ? `Created ${formatDate(row.created_at)}` : '';
}

const mergeHint = computed(() => {
  if (selected.value.length === 0)
    return 'Pick 2 records to merge — the rest of the list stays read-only.';
  if (selected.value.length === 1) {
    return config.value.mergeMatch
      ? 'Select 1 more of the same kind · 1 of 2 selected'
      : 'Select 1 more · 1 of 2 selected';
  }
  return '2 of 2 selected — ready to merge';
});

/**
 * The two records are snapshotted when the dialog opens, not read back off
 * `selected` — confirming a merge clears the selection, and the dialog is
 * still fading out at that point.
 */
const mergeLeft = ref<EntityRow | null>(null);
const mergeRight = ref<EntityRow | null>(null);

const mergeRows = computed(() => {
  const left = mergeLeft.value;
  const right = mergeRight.value;
  if (!left || !right) return [];
  // Only the fields this pair's kind shows (a party merges with its own kind).
  const keys = config.value.mergeFields.filter(key => {
    const field = allFields.value.find(candidate => candidate.key === key);
    return !field || isFieldShown(field, left);
  });
  return keys.map((key, index) => {
    const column = config.value.columns.find(candidate => candidate.key === key);
    const field = allFields.value.find(candidate => candidate.key === key);
    const leftValue = displayValue(left[key]);
    const rightValue = displayValue(right[key]);
    // Untouched, the merged value takes whichever record has one — the first
    // record's when both do. Tags take both records' tags, so none drop silently.
    const fallback =
      field?.kind === 'tags'
        ? joinTags([...new Set([...splitTags(leftValue), ...splitTags(rightValue)])])
        : leftValue || rightValue;
    const current = mergeValues.value[key] ?? fallback;
    // The first field stands in for the name — unless the config has its own
    // naming rule (a person needs *one of* four name parts), checked on confirm.
    const required = Boolean(field?.required) || (index === 0 && !config.value.validate);
    const identical = leftValue === rightValue;
    return {
      key,
      label: column?.label ?? field?.label ?? key,
      required,
      state:
        required && !current.trim()
          ? ('missing' as const)
          : identical && leftValue
            ? ('identical' as const)
            : !identical && leftValue && rightValue
              ? ('differs' as const)
              : undefined,
      leftValue,
      rightValue,
      value: current,
      placeholder: field?.placeholder ?? `Enter ${(column?.label ?? key).toLowerCase()}`,
      // The merged result is edited with the same control the create dialog
      // uses for that field — a `type` is a dropdown in both places, or the
      // merge would write a value its enum doesn't have.
      // No merge-able type has a multiselect or number; one would merge as plain text.
      kind:
        field?.kind === 'multiselect' || field?.kind === 'number'
          ? 'text'
          : (field?.kind ?? 'text'),
      options: field?.options ?? mergeOptions.value[key],
      upper: Boolean(field?.upper),
      maxLength: field?.maxLength,
      suggestions: field?.suggestions,
    };
  });
});

const mergeBlocked = computed(
  () => merging.value || mergeRows.value.some(row => row.state === 'missing')
);

/** Every configured field, for the merge dialog's per-key lookup. */
const allFields = computed<EntityField[]>(() => [
  ...config.value.fields.flat(),
  ...(config.value.collapsible?.fields ?? []),
]);

// --- data ------------------------------------------------------------------
/** `(Empty)` is a display value; the API wants the empty string for a null. */
function buildColumnFilters(): Record<string, string[]> {
  const filters: Record<string, string[]> = {};
  for (const [key, values] of Object.entries(columnFilters.value)) {
    if (Array.isArray(values) && values.length > 0) {
      filters[key] = values.map(value => (value === '(Empty)' ? '' : value));
    }
  }
  return filters;
}

async function fetchRows(): Promise<void> {
  loading.value = true;
  loadError.value = null;
  try {
    const query: FilterableSearchQuery = {
      text: searchQuery.value || '',
      limit: pageSize.value,
      offset: (currentPage.value - 1) * pageSize.value,
      sort_key: sortKey.value,
      sort_direction: sortDirection.value === 'asc' ? 'asc' : 'desc',
      similarity_threshold: 0.5,
      column_filters: buildColumnFilters(),
    };
    const result = await config.value.api.search(query, excludeOldRevisions.value);
    rows.value = result.results;
    totalCount.value = result.result_count;
    filterOptions.value = result.filter_options;
  } catch (error) {
    logger.error(`Error fetching ${config.value.plural.toLowerCase()}:`, error);
    loadError.value = `Couldn’t load ${config.value.plural.toLowerCase()}`;
  } finally {
    loading.value = false;
  }
}

// A new search starts from the first page and drops any manual sort. The field
// debounces its own keystrokes and fires this at once when it is cleared.
async function applySearch(): Promise<void> {
  currentPage.value = 1;
  sortActivated.value = false;
  await fetchRows();
}

watch([currentPage, pageSize], () => void fetchRows());
watch(columnFilters, () => {
  currentPage.value = 1;
  void fetchRows();
});
// Switching entity type reuses this component instance, so reset the list.
watch(
  () => props.entityType,
  () => {
    visibleKeys.value = defaultVisibleKeys();
    void loadFilterLabels();
    openFilterKeys.value = [];
    filterBarOpen.value = false;
    exitSelectMode();
    void fetchRows();
  }
);

onMounted(() => {
  visibleKeys.value = defaultVisibleKeys();
  void loadFilterLabels();
  void fetchRows();
});

// --- formatting ------------------------------------------------------------
const emptyCell = '—';

const displayValue = toText;

/** Date only — the mock's `date` columns (a person's date of birth). */
function formatDate(raw: unknown): string {
  const value = toText(raw);
  if (!value) return emptyCell;
  return new Date(value).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatDateTime(raw: unknown): string {
  const value = toText(raw);
  if (!value) return emptyCell;
  return new Date(value).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function cellText(row: EntityRow, column: EntityColumn): string {
  if (column.value) return column.value(row) || emptyCell;
  if (column.kind === 'datetime') return formatDateTime(row[column.key]);
  if (column.kind === 'date') return formatDate(row[column.key]);
  return displayValue(row[column.key]) || emptyCell;
}

/** The pills a `tags` cell draws; empty means the cell falls back to a dash. */
function cellTags(row: EntityRow, column: EntityColumn): string[] {
  const raw = row[column.key];
  return Array.isArray(raw) ? raw.map(tag => toText(tag)).filter(Boolean) : [];
}

/**
 * The first column is the record's name — ink and semibold; a `strong` column
 * is the secondary emphasis; everything else is muted. Straight from the mock.
 */
function cellTone(column: EntityColumn, first: boolean): 'ink' | 'text' | 'tertiary' {
  if (first) return 'ink';
  return column.kind === 'strong' ? 'text' : 'tertiary';
}

/**
 * What a record is called, for confirm strips and dialog titles. Read through
 * the first column, so a column with its own `value` reads the way it shows.
 */
function rowName(row: EntityRow): string {
  const first = config.value.columns[0];
  const name = first.value ? first.value(row) : toText(row[first.key]);
  return name || 'Untitled';
}

// --- toolbar handlers ------------------------------------------------------
// Also the empty state's "Clear search" button, which is why it stays a
// function rather than folding into the field's own clear.
function clearSearch(): void {
  searchQuery.value = '';
  currentPage.value = 1;
  void fetchRows();
}

/**
 * The Filter button is a toggle: opening it seeds the bar with the first
 * filterable column, closing it drops every active filter. The mock puts both
 * on the one control rather than a separate "clear".
 */
function toggleFilterBar(): void {
  const on = !(filterBarOpen.value || activeFilterCount.value > 0);
  if (!on) {
    filterBarOpen.value = false;
    openFilterKeys.value = [];
    openChip.value = null;
    setColumnFilters({});
    return;
  }
  filterBarOpen.value = true;
  if (!openFilterKeys.value.length && config.value.filters[0]) {
    openFilterKeys.value = [config.value.filters[0].key];
    openChip.value = config.value.filters[0].key;
  }
}

function addFilter(key: string): void {
  openFilterKeys.value = [...openFilterKeys.value, key];
  openChip.value = key;
}

function removeFilter(key: string): void {
  openFilterKeys.value = openFilterKeys.value.filter(open => open !== key);
  openChip.value = null;
  setColumnFilters({ ...columnFilters.value, [key]: [] });
}

function toggleFilterValue(key: string, value: string): void {
  const current = columnFilters.value[key] ?? [];
  const next = current.includes(value)
    ? current.filter(picked => picked !== value)
    : [...current, value];
  setColumnFilters({ ...columnFilters.value, [key]: next });
}

function clearAllFilters(): void {
  setColumnFilters({});
}

/** Display labels for coded filter values, per filter key — see `EntityFilter.labels`. */
const filterLabels = ref<Partial<Record<string, Partial<Record<string, string>>>>>({});

async function loadFilterLabels(): Promise<void> {
  filterLabels.value = {};
  const withLabels = config.value.filters.filter(filter => filter.labels);
  try {
    const entries = await Promise.all(
      withLabels.map(async filter => [filter.key, (await filter.labels?.()) ?? {}] as const)
    );
    filterLabels.value = Object.fromEntries(entries);
  } catch (error) {
    // Unlabelled is still usable — the chips fall back to the raw values.
    logger.error('Error loading filter labels:', error);
  }
}

function filterLabel(key: string, value: string): string {
  return filterLabels.value[key]?.[value] ?? value;
}

/** Options for one chip's panel: every value the backend reported, with the term applied. */
function filterValues(key: string): GaiaFilterValue[] {
  const term = (valueSearch.value[key] ?? '').trim().toLowerCase();
  const picked = columnFilters.value[key] ?? [];
  return (filterOptions.value[key] ?? [])
    .map(value => value ?? '(Empty)')
    .map(value => ({ key: value, label: filterLabel(key, value), checked: picked.includes(value) }))
    .filter(option => !term || option.label.toLowerCase().includes(term))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** The chip shows labels and emits the one removed; map it back to its raw value. */
function removeFilterLabel(key: string, label: string): void {
  const value = (columnFilters.value[key] ?? []).find(raw => filterLabel(key, raw) === label);
  if (value !== undefined) toggleFilterValue(key, value);
}

const availableFilters = computed(() =>
  config.value.filters.filter(filter => !openFilterKeys.value.includes(filter.key))
);

function toggleColumn(key: string): void {
  visibleKeys.value = visibleKeys.value.includes(key)
    ? visibleKeys.value.filter(visible => visible !== key)
    : [...visibleKeys.value, key];
}

function onRevisionToggle(value: boolean): void {
  excludeOldRevisions.value = value;
  currentPage.value = 1;
  void fetchRows();
}

function onSort(key: string): void {
  if (sortActivated.value && sortKey.value === key) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortActivated.value = true;
    sortKey.value = key;
    // A date column opens newest-first; everything else opens A–Z.
    const column = config.value.columns.find(candidate => candidate.key === key);
    sortDirection.value = column?.kind === 'datetime' || column?.kind === 'date' ? 'desc' : 'asc';
  }
  currentPage.value = 1;
  void fetchRows();
}

// --- merge handlers --------------------------------------------------------
function toggleSelectMode(mode: 'merge' | 'share'): void {
  const next = selectMode.value === mode ? null : mode;
  exitSelectMode();
  selectMode.value = next;
}

function exitSelectMode(): void {
  selectMode.value = null;
  selectedRows.value = [];
}

function toggleSelected(row: EntityRow): void {
  if (selected.value.includes(row.uid)) {
    selectedRows.value = selectedRows.value.filter(picked => picked.uid !== row.uid);
    return;
  }
  // Two is the whole point of merge mode — a third pick is silently refused,
  // as is one that can't merge with the first (a party of the other kind).
  if (mergeMode.value && (selected.value.length >= 2 || !canPick(row))) return;
  selectedRows.value = [...selectedRows.value, row];
}

/** The row can join the current pick under the config's `mergeMatch`. */
function canPick(row: EntityRow): boolean {
  const match = config.value.mergeMatch;
  const first = selectedRows.value.at(0);
  return !match || !first || match(first) === match(row);
}

/** Options for merge fields that load theirs (countries), fetched when the dialog opens. */
const mergeOptions = ref<Record<string, SelectOption[]>>({});

async function loadMergeOptions(): Promise<void> {
  const loaders = allFields.value.filter(
    field => field.loadOptions && config.value.mergeFields.includes(field.key)
  );
  try {
    const entries = await Promise.all(
      loaders.map(async field => [field.key, (await field.loadOptions?.()) ?? []] as const)
    );
    mergeOptions.value = Object.fromEntries(entries);
  } catch (error) {
    logger.error('Error loading merge options:', error);
  }
}

function openMergeModal(): void {
  mergeLeft.value = selectedRows.value.at(0) ?? null;
  mergeRight.value = selectedRows.value.at(1) ?? null;
  mergeValues.value = {};
  mergeShowEmpty.value = false;
  mergeOpen.value = true;
  void loadMergeOptions();
}

async function confirmMerge(): Promise<void> {
  const left = mergeLeft.value;
  const right = mergeRight.value;
  if (!left || !right || !config.value.api.merge) return;
  merging.value = true;
  try {
    // The pair's kind rides along so a kind-aware config knows which body to build.
    const merged: Record<string, unknown> = { kind: left.kind };
    for (const row of mergeRows.value) merged[row.key] = row.value;
    const ruleErrors = Object.values(
      config.value.validate?.(
        Object.fromEntries(Object.entries(merged).map(([key, value]) => [key, toText(value)]))
      ) ?? {}
    );
    if (ruleErrors.length) {
      message.error(ruleErrors[0]);
      return;
    }
    const survivor = await config.value.api.merge({
      leftUid: left.uid,
      rightUid: right.uid,
      mergedData: merged,
    });
    message.success(`${config.value.plural} merged successfully`);
    mergeOpen.value = false;
    exitSelectMode();
    // A merge that returns the new record opens it; the list refetches on
    // the way back, so the two deleted sources are gone from it.
    if (survivor) {
      await router.push(detailRoute(survivor));
      return;
    }
    await fetchRows();
  } catch (error) {
    logger.error('Error merging records:', error);
    message.error(error instanceof Error ? error.message : 'Failed to merge records');
  } finally {
    merging.value = false;
  }
}

// --- row handlers ----------------------------------------------------------
/** A click between the cells; a link inside the row handles its own. */
function openDetail(row: EntityRow, event: MouseEvent): void {
  if (picking.value) {
    toggleSelected(row);
    return;
  }
  if (event.target instanceof Element && event.target.closest('a')) return;
  void router.push(detailRoute(row));
}

/**
 * Where a cell links: the detail, unless the cell holds a link of its own (an
 * <a> can't nest) or the click picks the row instead.
 */
function cellDetailRoute(row: EntityRow, column: EntityColumn): RouteLocationRaw | undefined {
  if (picking.value) return undefined;
  if (column.link?.(row) && !mergeMode.value) return undefined;
  return detailRoute(row);
}

function detailRoute(row: EntityRow): RouteLocationRaw {
  return {
    name: 'contract-management-detail',
    params: { entityType: config.value.type, uid: row.uid },
  };
}

async function deleteRow(row: EntityRow): Promise<void> {
  await rowDelete.run(row, {
    work: async () => {
      await deleteEntity(config.value, row.uid);
      message.success(`${label.value} deleted successfully`);
      await fetchRows();
    },
    errorText: error =>
      error instanceof Error ? error.message : `Couldn’t delete this ${label.value.toLowerCase()}.`,
  });
}

// --- form handlers ---------------------------------------------------------
function openCreate(presetKey?: string): void {
  formDefaults.value =
    config.value.createOptions?.find(option => option.key === presetKey)?.defaults ?? {};
  formRow.value = null;
  formOpen.value = true;
}

function openEdit(row: EntityRow): void {
  formRow.value = row;
  formOpen.value = true;
}

// The row grid is structural CSS the design system
// has no primitive for; colours still come from the tokens.
const rowBorder = gaiaNeutral.borderSoft;
const rowHover = 'rgba(25,27,31,.025)';
const selectedRow = 'rgba(134,133,239,.06)';
/**
 * Surfaces inside the merge dialog. n-modal teleports its content to <body>,
 * outside this component's root — so the custom properties a scoped `v-bind()`
 * writes there never reach it, and these have to be inline.
 */
</script>

<template>
  <!-- Create / edit opens beside the list rather than over it: the layout's
       drawer squeezes the page, and the list stays usable meanwhile. -->
  <Layout
    :content-max-width="gaiaContentWidth.table"
    :drawer-open="formOpen"
    :drawer-width="config.formWidth"
  >
    <template #breadcrumb>
      <n-breadcrumb-item v-for="crumb in config.crumbs" :key="crumb">{{ crumb }}</n-breadcrumb-item>
      <n-breadcrumb-item>{{ config.plural }}</n-breadcrumb-item>
    </template>
    <n-flex vertical :size="16">
      <!-- Title, count, then the toolbar pushed right. -->
      <n-flex align="flex-start" :size="[20, 12]">
        <n-flex align="baseline" :size="10" :style="{ minWidth: 0 }">
          <gaia-header :level="1" variant="display" size="screen" :style="{ margin: 0 }">
            {{ config.plural }}
          </gaia-header>
          <gaia-count-label>{{ countLabel }}</gaia-count-label>
        </n-flex>

        <!-- One line, always: when the side panel narrows the page, the search
             field gives up width rather than a button dropping to a new row. -->
        <n-flex
          align="center"
          justify="end"
          :size="8"
          :wrap="false"
          :style="{ marginLeft: 'auto', minWidth: 0 }"
        >
          <!-- The mock's toolbar has two heights: 36px for the secondary
               controls, taller for the primary CTA. The toolbar variant is the
               36px step. -->
          <gaia-search-input
            v-model:value="searchQuery"
            :style="{ flex: '0 1 264px', minWidth: '140px' }"
            :placeholder="config.searchPlaceholder"
            :debounce="500"
            @search="applySearch"
          />

          <gaia-button size="medium" @click="toggleFilterBar">
            <template #icon>
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
              >
                <path d="M2.5 4h11M4.5 8h7M6.5 12h3" />
              </svg>
            </template>
            Filter
            <gaia-count-pill v-if="activeFilterCount" :style="{ marginLeft: '7px' }">
              {{ activeFilterCount }}
            </gaia-count-pill>
          </gaia-button>

          <gaia-popover placement="bottom-end">
            <template #trigger>
              <gaia-icon-button :dimension="36" title="Columns and options">
                <n-icon :component="IconMore" :size="18" />
              </gaia-icon-button>
            </template>
            <gaia-columns-menu
              :columns="columnOptions"
              :toggle-label="config.revisions ? 'Exclude old revisions' : undefined"
              :toggle-value="excludeOldRevisions"
              toggle-hint="Hides entities extracted from superseded contract revisions."
              :action-label="
                config.api.merge ? (mergeMode ? 'Exit merge mode' : 'Merge records') : undefined
              "
              :action-active="mergeMode"
              @toggle="toggleColumn"
              @update:toggle-value="onRevisionToggle"
              @action="toggleSelectMode('merge')"
            />
          </gaia-popover>

          <gaia-button
            v-if="canShare"
            size="medium"
            :type="selectMode === 'share' ? 'primary' : 'default'"
            @click="toggleSelectMode('share')"
          >
            <template #icon><n-icon :component="IconShare" /></template>
            Share
          </gaia-button>

          <!-- Slotted: the label is per-entity config, already spelt
               "Create <noun>". -->
          <gaia-dropdown
            v-if="createMenuOptions.length"
            :options="createMenuOptions"
            placement="bottom-end"
            @select="openCreate"
          >
            <gaia-create-button>{{ config.createLabel }}</gaia-create-button>
          </gaia-dropdown>
          <gaia-create-button v-else @click="openCreate()">{{
            config.createLabel
          }}</gaia-create-button>
        </n-flex>
      </n-flex>

      <!-- Filter bar: one chip per opened column, each with its value panel. -->
      <gaia-filter-bar
        v-if="filterBarOpen || activeFilterCount"
        :can-add="availableFilters.length > 0"
        :show-reset="activeFilterCount > 0"
        @reset="clearAllFilters"
      >
        <gaia-popover
          v-for="key in openFilterKeys"
          :key="key"
          placement="bottom-start"
          :show="openChip === key"
          @update:show="(show: boolean) => (openChip = show ? key : null)"
        >
          <template #trigger>
            <gaia-filter-chip
              :field="config.filters.find(filter => filter.key === key)?.label ?? key"
              :values="(columnFilters[key] ?? []).slice(0, 3).map(value => filterLabel(key, value))"
              :overflow="
                (columnFilters[key] ?? []).length > 3
                  ? `+${(columnFilters[key] ?? []).length - 3}`
                  : undefined
              "
              :active="openChip === key"
              @toggle="openChip = openChip === key ? null : key"
              @remove="removeFilter(key)"
              @remove-value="(label: string) => removeFilterLabel(key, label)"
            />
          </template>
          <gaia-filter-value-panel
            :values="filterValues(key)"
            :search="valueSearch[key] ?? ''"
            :picked-label="
              (columnFilters[key] ?? []).length
                ? `${(columnFilters[key] ?? []).length} ${(columnFilters[key] ?? []).length === 1 ? 'value' : 'values'} selected`
                : undefined
            "
            @update:search="(term: string) => (valueSearch[key] = term)"
            @toggle="(value: string) => toggleFilterValue(key, value)"
            @clear="setColumnFilters({ ...columnFilters, [key]: [] })"
          />
        </gaia-popover>

        <!-- "Add filter" opens the list of columns not already on the bar. -->
        <template #add>
          <gaia-dropdown
            :options="availableFilters.map(filter => ({ key: filter.key, label: filter.label }))"
            placement="bottom-start"
            @select="addFilter"
          >
            <gaia-add-button subject="filter" variant="dashed" />
          </gaia-dropdown>
        </template>
      </gaia-filter-bar>

      <!-- Merge mode replaces nothing — it sits above the table as its own bar. -->
      <gaia-selection-bar
        v-if="mergeMode"
        label="Merge mode"
        :hint="mergeHint"
        @clear="exitSelectMode"
      >
        <gaia-button
          type="primary"
          size="small"
          :disabled="selected.length !== 2 || selectionAdminBlock !== null"
          :title="selectionAdminBlock ?? undefined"
          @click="openMergeModal"
        >
          Merge records
        </gaia-button>
      </gaia-selection-bar>

      <gaia-selection-bar
        v-else-if="selectMode === 'share'"
        label="Share mode"
        :hint="
          selected.length
            ? `${selected.length} selected`
            : 'Pick the records to share with users and roles.'
        "
        @clear="exitSelectMode"
      >
        <gaia-button
          type="primary"
          size="small"
          :disabled="selected.length === 0 || selectionAdminBlock !== null"
          :title="selectionAdminBlock ?? undefined"
          @click="shareOpen = true"
        >
          Share {{ selected.length || '' }}
        </gaia-button>
      </gaia-selection-bar>

      <gaia-data-table
        :columns="tableColumns"
        :sort-key="sortKey"
        :sort-direction="sortDirection === 'asc' ? 'asc' : 'desc'"
        :sort-active="sortActivated"
        :lead-column="picking"
        @sort="onSort"
      >
        <template #default="{ rowStyle }">
          <!-- Loading: the toolbar and header stay live, the body shimmers. -->
          <gaia-table-skeleton
            v-if="loading"
            :row-style="rowStyle"
            :cells="skeletonCells"
            :rows="6"
            :lead="picking"
          />

          <gaia-empty-state v-else-if="loadError" status="alert" :title="loadError">
            The server didn’t respond in time. Nothing was changed — retrying is safe.
            <template #actions>
              <gaia-button type="primary" size="small" @click="fetchRows">Retry</gaia-button>
            </template>
          </gaia-empty-state>

          <gaia-empty-state v-else-if="showEmpty" :title="config.emptyTitle">
            <template #icon>
              <svg
                width="18"
                height="18"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path :d="config.icon" />
              </svg>
            </template>
            {{ config.emptyBody }}
            <template #actions>
              <gaia-dropdown
                v-if="createMenuOptions.length"
                :options="createMenuOptions"
                @select="openCreate"
              >
                <gaia-create-button :icon="false">{{ config.createLabel }}</gaia-create-button>
              </gaia-dropdown>
              <gaia-create-button v-else :icon="false" @click="openCreate()">
                {{ config.createLabel }}
              </gaia-create-button>
            </template>
          </gaia-empty-state>

          <gaia-empty-state v-else-if="showNoMatch" :title="noMatchTitle">
            <template #icon>
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
              >
                <circle cx="8" cy="8" r="5" />
                <path d="M11.8 11.8 15.5 15.5" />
              </svg>
            </template>
            Try a shorter term, or clear the filters to see all {{ config.unit[1] }}.
            <template #actions>
              <gaia-button v-if="hasQuery" size="small" @click="clearSearch"
                >Clear search</gaia-button
              >
              <gaia-button v-if="activeFilterCount" size="small" @click="clearAllFilters">
                Clear filters
              </gaia-button>
            </template>
          </gaia-empty-state>

          <template v-else-if="showRows">
            <template v-for="row in rows" :key="row.uid">
              <!-- A row in trouble is replaced by its strip, never stacked with it. -->
              <gaia-table-row-state
                v-if="rowDelete.stateOf(row.uid) === 'confirm'"
                variant="confirm"
                :title="`Delete “${rowName(row)}”?`"
                @cancel="rowDelete.cancel()"
                @confirm="deleteRow(row)"
              >
                The record is removed everywhere. Contract files that reference it keep their text.
              </gaia-table-row-state>

              <gaia-table-row-state
                v-else-if="rowDelete.stateOf(row.uid) === 'busy'"
                variant="busy"
                note="Undo not available"
              >
                Deleting “{{ rowName(row) }}”…
              </gaia-table-row-state>

              <gaia-table-row-state
                v-else-if="rowDelete.stateOf(row.uid) === 'error'"
                variant="error"
                @retry="deleteRow(row)"
                @dismiss="rowDelete.dismissError()"
              >
                {{ rowDelete.errorTextOf(row.uid) }}
              </gaia-table-row-state>

              <div
                v-else
                class="row"
                :class="{ picked: selected.includes(row.uid) }"
                :style="rowStyle"
                @click="openDetail(row, $event)"
              >
                <gaia-checkbox
                  v-if="picking"
                  :checked="selected.includes(row.uid)"
                  :disabled="
                    mergeMode &&
                    !selected.includes(row.uid) &&
                    (selected.length >= 2 || !canPick(row))
                  "
                  size="small"
                  @click.stop
                  @update:checked="toggleSelected(row)"
                />

                <!-- Each cell without a link of its own is a link to the detail, so
                     middle / Cmd-click and "Open in new tab" work. The row itself
                     can't be one <a>: it holds links and buttons. -->
                <component
                  :is="cellDetailRoute(row, column) ? RouterLink : 'div'"
                  v-for="(column, position) in visibleColumns"
                  :key="column.key"
                  :to="cellDetailRoute(row, column)"
                  class="cell"
                >
                  <n-icon
                    v-if="column.icon?.(row)"
                    :component="column.icon(row)"
                    :size="15"
                    :depth="3"
                    style="flex: none"
                  />
                  <gaia-tag v-if="column.kind === 'chip' && row[column.key]" variant="label">
                    {{ cellText(row, column) }}
                  </gaia-tag>
                  <!-- A tags cell is a run of pills, not one truncated string. -->
                  <template v-else-if="column.kind === 'tags' && cellTags(row, column).length">
                    <gaia-tag
                      v-for="tag in cellTags(row, column)"
                      :key="tag"
                      variant="muted"
                      pill
                      size="tiny"
                    >
                      {{ tag }}
                    </gaia-tag>
                  </template>
                  <!-- n-ellipsis clips and shows the full value on hover, but only
                       when it is actually cut off. Plain text inside it: an n-text
                       there would be cloned into the dark tooltip. -->
                  <gaia-text
                    v-else
                    :size="position === 0 ? 'body' : 'dense'"
                    :tone="cellTone(column, position === 0)"
                    :weight="position === 0 || column.kind === 'strong' ? 'semibold' : 'regular'"
                    :style="{ display: 'block', minWidth: 0, fontVariantNumeric: 'tabular-nums' }"
                  >
                    <n-ellipsis :tooltip="{ style: { maxWidth: '360px' } }" style="max-width: 100%">
                      <gaia-link
                        v-if="column.link?.(row) && !mergeMode"
                        :underline="false"
                        :to="column.link(row)!"
                        @click.stop
                      >
                        {{ cellText(row, column) }}
                      </gaia-link>
                      <template v-else>{{ cellText(row, column) }}</template>
                    </n-ellipsis>
                  </gaia-text>
                </component>

                <n-flex justify="end" :size="2" :wrap="false">
                  <gaia-icon-button
                    :dimension="28"
                    quaternary
                    :title="deniedReason(row.permissions, ACCESS_PERMISSION_WRITE) ?? 'Edit'"
                    :disabled="!allows(row.permissions, ACCESS_PERMISSION_WRITE)"
                    @click.stop="openEdit(row)"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.4"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M11.2 2.6 13.4 4.8 5.6 12.6 2.6 13.4l.8-3z" />
                    </svg>
                  </gaia-icon-button>
                  <gaia-delete-button
                    size="md"
                    :disabled="!allows(row.permissions, ACCESS_PERMISSION_ADMIN)"
                    :title="deniedReason(row.permissions, ACCESS_PERMISSION_ADMIN) ?? undefined"
                    @click.stop="rowDelete.arm(row)"
                  />
                </n-flex>
              </div>
            </template>
          </template>
        </template>

        <template v-if="showRows" #footer>
          <gaia-pagination
            :page="currentPage"
            :page-size="pageSize"
            :total="totalCount"
            @update:page="(page: number) => (currentPage = page)"
            @update:page-size="(size: number) => (pageSize = size)"
          />
        </template>
      </gaia-data-table>
    </n-flex>

    <mass-share-contracts-modal
      v-if="canShare"
      v-model:show="shareOpen"
      kind="party"
      :contract-uids="selected"
      :contract-names="selectedNames"
      @shared="exitSelectMode"
    />

    <!-- Merge: build the surviving record field by field. -->
    <gaia-modal
      v-model:show="mergeOpen"
      width="900px"
      :closable="false"
      content-style="padding: 20px"
    >
      <n-flex align="flex-start" :size="12" :wrap="false">
        <div>
          <gaia-text size="subtitle" weight="semibold" tone="ink">
            Merge {{ config.plural.toLowerCase() }}
          </gaia-text>
          <gaia-text
            size="body"
            tone="text"
            :style="{ display: 'block', lineHeight: 1.55, marginTop: '6px' }"
          >
            Where the two records differ, choose the value to keep — or type your own. Both records
            are replaced by one new record.
          </gaia-text>
        </div>
        <gaia-icon-button
          :dimension="26"
          quaternary
          title="Close"
          :style="{ marginLeft: 'auto' }"
          @click="mergeOpen = false"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
          >
            <path d="M4 4l8 8M12 4l-8 8" />
          </svg>
        </gaia-icon-button>
      </n-flex>

      <div class="merge-table" :style="{ borderTop: mergeRule }">
        <!-- Column header: each record by name, so the radios below read as
             "keep this one's value". -->
        <div
          class="merge-head"
          :style="{ gridTemplateColumns: GAIA_MERGE_COLUMNS, borderBottom: mergeRule }"
        >
          <gaia-text variant="label" size="nano" tone="subtle">Field</gaia-text>
          <div v-for="(record, index) in [mergeLeft, mergeRight]" :key="index" class="merge-record">
            <gaia-text
              truncate
              size="dense"
              weight="semibold"
              tone="ink"
              :style="{ display: 'block' }"
            >
              {{ record ? rowName(record) : '' }}
            </gaia-text>
            <gaia-text size="micro" tone="subtle" :style="{ display: 'block', marginTop: '2px' }">
              {{ mergeRecordMeta(record) }}
            </gaia-text>
          </div>
          <div style="min-width: 0">
            <gaia-text size="dense" weight="semibold" tone="ink" :style="{ display: 'block' }">
              Merged record
            </gaia-text>
            <gaia-text size="micro" tone="subtle" :style="{ display: 'block', marginTop: '2px' }">
              Saved as a new record
            </gaia-text>
          </div>
        </div>

        <div class="merge-body">
          <template
            v-for="group in [
              { key: 'conflicts', title: 'Different values', rows: mergeGroups.conflicts },
              {
                key: 'filled',
                title: mergeGroups.conflicts.length ? 'Other fields' : 'Fields',
                rows: mergeGroups.filled,
              },
              {
                key: 'empty',
                title: 'Empty in both',
                rows: mergeShowEmpty ? mergeGroups.empty : [],
              },
            ]"
            :key="group.key"
          >
            <template v-if="group.rows.length">
              <n-flex align="baseline" :size="8" class="merge-group">
                <gaia-text variant="label" size="nano" tone="subtle">{{ group.title }}</gaia-text>
                <gaia-text size="micro" tone="faint">{{ group.rows.length }}</gaia-text>
              </n-flex>
              <gaia-merge-field
                v-for="field in group.rows"
                :key="field.key"
                :label="field.label"
                :required="field.required"
                :state="field.state"
                :left-value="field.leftValue"
                :right-value="field.rightValue"
                :value="field.value"
                :placeholder="field.placeholder"
                :kind="field.kind"
                :options="field.options"
                :upper="field.upper"
                :max-length="field.maxLength"
                :suggestions="field.suggestions"
                @update:value="(value: string) => (mergeValues[field.key] = value)"
                @copy-left="mergeValues[field.key] = field.leftValue"
                @copy-right="mergeValues[field.key] = field.rightValue"
              />
            </template>
          </template>

          <div v-if="mergeGroups.empty.length" class="merge-group">
            <gaia-link :underline="false" @click="mergeShowEmpty = !mergeShowEmpty">
              {{
                mergeShowEmpty
                  ? 'Hide empty fields'
                  : `Show ${mergeGroups.empty.length} empty ${mergeGroups.empty.length === 1 ? 'field' : 'fields'}`
              }}
            </gaia-link>
          </div>
        </div>
      </div>

      <n-flex align="center" :size="10" :wrap="false" :style="{ marginTop: '14px' }">
        <gaia-text size="micro" tone="subtle">
          {{ mergeBlocked && !merging ? 'Fill the required fields to merge.' : mergeCarryNote }}
        </gaia-text>
        <gaia-button size="small" :style="{ marginLeft: 'auto' }" @click="mergeOpen = false">
          Cancel
        </gaia-button>
        <gaia-button
          type="primary"
          size="small"
          :disabled="mergeBlocked"
          :loading="merging"
          @click="confirmMerge"
        >
          {{ merging ? 'Merging…' : 'Merge records' }}
        </gaia-button>
      </n-flex>
    </gaia-modal>
    <template #drawer>
      <entity-form-dialog
        v-model:show="formOpen"
        inline
        :config="config"
        :row="formRow"
        :row-name="formRow ? rowName(formRow) : ''"
        :defaults="formDefaults"
        @saved="fetchRows"
      />
    </template>
  </Layout>
</template>

<style scoped>
/* A body row rides the same grid template the header does — GaiaDataTable hands
   it out through the slot, so only the row's own chrome lives here. */
.row {
  display: grid;
  align-items: center;
  gap: 16px;
  /* Grows past 56px only when a cell stacks lines (a date's contract files). */
  box-sizing: border-box;
  min-height: 56px;
  padding: 8px 18px;
  border-bottom: 1px solid v-bind(rowBorder);
  cursor: pointer;
}

.row:hover {
  background: v-bind(rowHover);
}

.row.picked {
  background: v-bind(selectedRow);
}

.cell {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

/* A cell that is the detail link: no link styling, and the full row height
   so the link area isn't just the text line. */
a.cell {
  align-self: stretch;
  color: inherit;
  text-decoration: none;
}

/* The merge dialog is a plain table: a header row over the field rows, with
   the columns shared through GAIA_MERGE_COLUMNS. */
.merge-table {
  margin-top: 18px;
}

.merge-head {
  display: grid;
  gap: 16px;
  align-items: end;
  padding: 12px 4px 10px;
}

/* Record names start where their values do — past the radio's inset. */
.merge-record {
  min-width: 0;
  padding: 0 10px;
}

.merge-body {
  max-height: 52vh;
  overflow-y: auto;
}

.merge-group {
  display: flex;
  padding: 16px 4px 4px;
}
</style>
